import { useState, useEffect, useRef, ComponentType, lazy, Suspense } from 'react';

interface LazyComponentOptions {
  rootMargin?: string;
  threshold?: number;
}

/**
 * Hook that only loads a component when it's about to be visible in the viewport.
 * This removes components from the critical request chain.
 */
export function useLazyComponent<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: LazyComponentOptions = {}
) {
  const { rootMargin = '200px', threshold = 0 } = options;
  const [isVisible, setIsVisible] = useState(false);
  const [Component, setComponent] = useState<T | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin, threshold]);

  useEffect(() => {
    if (isVisible && !Component) {
      importFn().then((module) => {
        setComponent(() => module.default);
      });
    }
  }, [isVisible, Component, importFn]);

  return { ref, Component, isVisible };
}

/**
 * Component wrapper that loads children only when visible
 */
interface LazyWhenVisibleProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  rootMargin?: string;
}

export function LazyWhenVisible({ 
  children, 
  fallback = <div className="h-96" />,
  rootMargin = '300px'
}: LazyWhenVisibleProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref}>
      {isVisible ? children : fallback}
    </div>
  );
}
